import { useLazyQuery, useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { isToday, isYesterday } from "date-fns";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedDate, useIntl } from "react-intl";

import Activity from "@components/Activity";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/expertJourney/ExpertJourneyActivitiesPage.module.css";
import ActivityFilters from "@components/forms/ActivityFilters";
import HandleQuery from "@components/HandleQuery";
import InfiniteScroll from "@components/InfiniteScroll";
import ExpertJourneyTablesPage from "@components/pageTypes/ExpertJourneyTablesPage";
import LargeText from "@components/text/LargeText";
import useSubscribeToExpertActivities from "@hooks/subscriptions/useSubscribeToExpertActivities";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { OtherActivityKindsEnum, StartTimeValuesEnum } from "@typing/Enums";
import {
  ActivityActorKindEnum,
  ActivityFragment as ActivityType,
  ClientRoleRoleEnum,
  GetActivitiesDocument,
  GetActivitiesQuery,
  GetActivitiesQueryVariables,
  GetActivitySummaryDocument,
  GetActivitySummaryQuery,
  GetActivitySummaryQueryVariables,
  GetJourneyWithClientRolesDocument,
  GetJourneyWithClientRolesQuery,
  GetJourneyWithClientRolesQueryVariables
} from "@typing/Generated";
import {
  calculateActivityKindsFromEnum,
  calculateStartTimeFromEnum,
  filterActivityGroupWithOneArticle,
  groupByTimestampDay,
  primaryExpertRole
} from "@utils/modelUtils";

type Props = {
  journeyId: string;
};

type Filters = ComponentProps<typeof ActivityFilters>["filters"];

const ExpertJourneyActivitiesPage = ({ journeyId }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [getActivitySummary, getActivitySummaryQuery] = useLazyQuery<
    GetActivitySummaryQuery,
    GetActivitySummaryQueryVariables
  >(GetActivitySummaryDocument);

  const [getActivities, getActivitiesQuery] = useLazyQuery<GetActivitiesQuery, GetActivitiesQueryVariables>(
    GetActivitiesDocument
  );

  useSubscribeToExpertActivities(myExpert?.id, () => {
    getActivitiesQuery.refetch();
    getActivitySummaryQuery.refetch();
  });

  const getJourneyQuery = useQuery<GetJourneyWithClientRolesQuery, GetJourneyWithClientRolesQueryVariables>(
    GetJourneyWithClientRolesDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const [filters, setFilters] = useState<Filters>({
    activityKind: OtherActivityKindsEnum.ALL,
    actorKind: ActivityActorKindEnum.CLIENTS,
    clientId: null,
    section: null,
    startTime: StartTimeValuesEnum.PAST_SEVEN_DAYS
  });

  const primaryExpert = useMemo(
    () => primaryExpertRole(getJourneyQuery.data?.journey.expertRoles)?.expert,
    [getJourneyQuery.data?.journey.expertRoles]
  );

  const ownerClientRole = useMemo(
    () => getJourneyQuery.data?.journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.OWNER),
    [getJourneyQuery.data?.journey.clientRoles]
  );

  useEffect(() => {
    if (primaryExpert && ownerClientRole) {
      getActivitySummary({
        variables: {
          clientId: ownerClientRole.client.id,
          expertId: primaryExpert.id,
          startTime: calculateStartTimeFromEnum(filters.startTime)
        }
      });
    }
  }, [filters.startTime, getActivitySummary, ownerClientRole, primaryExpert]);

  useEffect(() => {
    if (primaryExpert && ownerClientRole) {
      getActivities({
        variables: {
          activityKinds: calculateActivityKindsFromEnum(filters.activityKind),
          actorKind: filters.actorKind,
          clientId: ownerClientRole.client.id,
          expertId: primaryExpert.id,
          section: filters.section,
          startTime: calculateStartTimeFromEnum(filters.startTime)
        }
      });
    }
  }, [filters, getActivities, primaryExpert, ownerClientRole]);

  const allActivities = useNodesFromConnection(getActivitiesQuery.data?.activities);
  const activitiesGrouped = useMemo(
    () => groupByTimestampDay<ActivityType>(allActivities, activity => activity.activeAt, intl),
    [allActivities, intl]
  );

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const dateForGroup = useCallback(
    (date: Date) => {
      if (isToday(date)) {
        return intl.formatMessage(
          { id: "pages.expert.activity.days.today" },
          { date: <FormattedDate day="numeric" month="long" value={date} /> }
        );
      }
      if (isYesterday(date)) {
        return intl.formatMessage(
          { id: "pages.expert.activity.days.yesterday" },
          { date: <FormattedDate day="numeric" month="long" value={date} /> }
        );
      }
      return <DateStamp value={date} />;
    },
    [intl]
  );

  return (
    <ExpertJourneyTablesPage
      helmetKey="pageTitles.expertJourney.activity.list"
      imageName="womanWithCoffee"
      journeyId={journeyId}
      titleKey="pages.expert.activity.activity"
    >
      <HandleQuery query={getActivitySummaryQuery}>
        {activitySummaryData => (
          <HandleQuery query={getActivitiesQuery}>
            {activitiesData => (
              <>
                <ActivityFilters
                  activitySummary={activitySummaryData.activitySummary}
                  clientRoles={getJourneyQuery.data?.journey.clientRoles}
                  filters={filters}
                  inDashboard={false}
                  onChange={handleFilterChange}
                />
                <InfiniteScroll loadMore={getActivitiesQuery.fetchMore} pageInfo={activitiesData?.activities.pageInfo}>
                  <div className={styles.activitiesContainer}>
                    {activitiesGrouped.map(group => (
                      <div className={styles.group} key={`${group.year}-${group.month}-${group.date}`}>
                        <IonGrid className="ion-no-padding">
                          <IonRow className="ion-no-padding">
                            <IonCol>
                              <LargeText className={styles.owner}>{dateForGroup(group.models[0].activeAt)}</LargeText>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                        {filterActivityGroupWithOneArticle(group).models.map(activity => (
                          <Activity activitiesForGroup={group.models} activity={activity} key={activity.id} />
                        ))}
                      </div>
                    ))}
                  </div>
                </InfiniteScroll>
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyTablesPage>
  );
};

export default ExpertJourneyActivitiesPage;
